import { createRecipeComponentsState } from '../../hooks/useInventoryPageState'
import { convertInventoryQuantity } from '../../utils/inventoryUnits'
import {
  getReadableUnitLabel,
  resolveItemUnit,
  toBoolean
} from '../../utils/inventoryResolvers'

export const formatRecipeValue = value => {
  if (value === undefined || value === null || value === '') return '-'

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)

  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  }).format(numericValue)
}

export const getUnitContextText = unit => getReadableUnitLabel(unit, 2) || 'sin unidad'

export const resolveRecipeComponentItemId = row =>
  Number(row?.component_item_id ?? row?.componentItemId ?? row?.item_id ?? row?.itemId ?? 0)

export const resolveRecipeComponentUnit = (row, itemOptions) => {
  const directUnit = resolveItemUnit(row, '')
  if (directUnit) return directUnit

  const componentId = resolveRecipeComponentItemId(row)
  if (componentId > 0) {
    const optionById = itemOptions.find(option => Number(option.id) === componentId)
    if (optionById?.unit) return optionById.unit
  }

  const componentSku = String(row?.component_sku ?? row?.componentSku ?? row?.sku ?? '').trim().toUpperCase()
  if (componentSku) {
    const optionBySku = itemOptions.find(
      option => String(option.sku || '').trim().toUpperCase() === componentSku
    )
    if (optionBySku?.unit) return optionBySku.unit
  }

  return ''
}

export const getRecipeComponentLabel = (row, itemOptions) => {
  const directSku = String(row?.component_sku ?? row?.componentSku ?? row?.sku ?? '').trim()
  const directName = String(row?.component_name ?? row?.componentName ?? row?.name ?? '').trim()

  if (directSku || directName) {
    return [directSku, directName].filter(Boolean).join(' · ')
  }

  const componentId = resolveRecipeComponentItemId(row)
  if (componentId > 0) {
    const optionById = itemOptions.find(option => Number(option.id) === componentId)
    if (optionById) {
      return [optionById.sku, optionById.name].filter(Boolean).join(' · ') || 'Ingrediente sin nombre'
    }
  }

  return 'Ingrediente sin nombre'
}

const toFormFieldValue = value => {
  if (value === undefined || value === null || value === '') return ''
  return String(value)
}

const getNextRecipeVersion = version => {
  const normalizedVersion = String(version ?? '').trim()
  if (!normalizedVersion) return '1'

  const numericVersion = Number(normalizedVersion)
  if (Number.isFinite(numericVersion)) return String(numericVersion + 1)

  return normalizedVersion
}

export const buildRecipeFormFromRecipe = recipe => ({
  version: getNextRecipeVersion(recipe?.version),
  yieldQty: toFormFieldValue(recipe?.yield_qty ?? recipe?.yieldQty ?? 1),
  active: toBoolean(recipe?.active ?? recipe?.is_active ?? recipe?.isActive ?? true)
})

export const buildRecipeComponentsFromRows = (rows, itemOptions) => {
  const components = rows
    .map((row, index) => {
      const componentItemId = resolveRecipeComponentItemId(row)
      const baseUnit = resolveRecipeComponentUnit(row, itemOptions)
      const displayUnit = String(row?.qty_unit ?? row?.qtyUnit ?? '').trim().toUpperCase() || baseUnit
      const normalizedQty = row?.qty_required ?? row?.qtyRequired ?? ''
      const displayQty = displayUnit
        ? convertInventoryQuantity(normalizedQty, baseUnit, displayUnit)
        : normalizedQty

      return {
        componentItemId: componentItemId > 0 ? String(componentItemId) : '',
        qtyRequired: toFormFieldValue(displayQty),
        displayQuantity: toFormFieldValue(displayQty),
        displayUnit,
        wastePct: toFormFieldValue(row?.waste_pct ?? row?.wastePct ?? ''),
        sortOrder: toFormFieldValue(row?.sort_order ?? row?.sortOrder ?? index + 1)
      }
    })
    .filter(component => component.componentItemId)

  return components.length > 0 ? components : createRecipeComponentsState()
}
